import * as React from 'react'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import CampaignSelect from '@user/components/Leads/AddLeadPage/FormFields/CampaignSelect/CampaignSelect'
import DivisionSelect from '@user/components/Leads/AddLeadPage/FormFields/DivisionSelect/DivisionSelect'
import { LeadSourceSelect } from '@user/components/Leads/AddLeadPage/FormFields/LeadSourceSelect/LeadSourceSelect'
import LocationTypeSelect from '@user/components/Leads/AddLeadPage/FormFields/LocationTypeSelect/LocationTypeSelect'
import { TargetPestSelect } from '@user/components/Formik/TargetPestSelect/TargetPestSelect'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { selectAllDivisions } from '@user/store/divisions/slice'
import { selectAllLocationTypes } from '@user/store/locationTypes/slice'

import type { AdditionalMissingKey } from '../typings'
import { AddressFields } from './AddressFields'
import { RequiredTextField } from './RequiredTextField'
import { UseReviewNote } from './UseReviewNote'
import { useMirrorFormValue } from './hooks/useMirrorFormValue'

interface Props {
  fieldKey: AdditionalMissingKey
}

/**
 * Renders the input for one built-in Additional Field the server reported missing. Option lists
 * come from the store (divisions, location types, campaigns, lead sources, target pests) and must
 * be loaded by the caller. The Add Lead selects keep their own keys, so lead source and target
 * pests are mirrored onto the modal's `sourceId` and `targetPestIds`. Referral cannot be edited
 * here and shows a note instead.
 */
export function AdditionalLeadField(props: Props) {
  const { fieldKey } = props
  const tidalThemeActive = useTidalThemeActive()
  const density = tidalThemeActive ? 'normal' : 'compact'

  const allDivisions = useSelector(selectAllDivisions)
  const allLocationTypes = useSelector(selectAllLocationTypes)
  const divisions = useMemo(
    () => allDivisions.filter((division) => division.active),
    [allDivisions],
  )
  const locationTypes = useMemo(
    () => allLocationTypes.filter((type) => type.active),
    [allLocationTypes],
  )

  useMirrorFormValue('leadSourceId', 'sourceId')
  useMirrorFormValue('targetPests', 'targetPestIds')

  switch (fieldKey) {
    case 'divisionId':
      return (
        <DivisionSelect
          name="divisionId"
          label="Division"
          divisions={divisions}
          show
          required
          density={density}
        />
      )
    case 'altPhoneNumber':
      return (
        <RequiredTextField
          name="altPhoneNumber"
          label="Alternate Phone Number"
          testId="auto-review-alt-phone-number"
        />
      )
    case 'address':
      return <AddressFields />
    case 'locationTypeId':
      return (
        <LocationTypeSelect
          name="locationTypeId"
          label="Location Type"
          locationTypes={locationTypes}
          show
          required
          density={density}
        />
      )
    case 'note':
      return (
        <RequiredTextField
          name="note"
          label="Note"
          testId="auto-review-note"
          multiline
        />
      )
    case 'targetPests':
      return (
        <TargetPestSelect
          name="targetPests"
          required
          show
          showCheckBox
          density={density}
        />
      )
    case 'campaignId':
      return (
        <CampaignSelect
          name="campaignId"
          label="Campaign"
          leadSourceRequired
          required
          show
          density={density}
          disabled={false}
        />
      )
    case 'leadSourceId':
      return (
        <LeadSourceSelect
          name="leadSourceId"
          label="Lead Source"
          required
          show
          density={density}
          disabled={false}
        />
      )
    case 'referral':
      return <UseReviewNote subject="the referral" />
    default:
      return null
  }
}
AdditionalLeadField.displayName = 'AdditionalLeadField'
